<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col md="12">
          <h5 class="section-label mb-3">
            {{ $t('Savings Pocket Setup') }}
          </h5>
        </b-col>
        <b-col
          md="12"
          class="align-items-center justify-content-start mb-1 mb-md-0"
        >
          <validation-observer ref="savingsPocketRule">
            <b-form @submit.prevent="handleSubmit">
              <b-row
                v-for="(item, index) in savingsPocketFormData"
                :key="index"
                class="match-height"
              >

                <b-col :md="isUpdateMode ? '3' : '4'">
                  <SelectField
                    id="savings-type"
                    v-model="item.savingsType"
                    :label="$t('Savings Type')"
                    :options="savingsTypeOptions"
                    :error-name="$t('Savings Type')"
                    rules="required"
                    :reduce="option => option.value"
                    item="label"
                    :placeholder="$t('Select')"
                    readonly
                  />
                </b-col>
                <!-- Savings Name -->

                <!-- Product -->
                <b-col :md="isUpdateMode ? '3' : '4'">
                  <SelectField
                    id="product"
                    v-model="item.productCode"
                    :label="$t('Product')"
                    :options="getProductOptions(item.savingsType)"
                    :error-name="$t('Product')"
                    rules="required"
                    :reduce="option => option.value"
                    item="label"
                    :placeholder="$t('Select')"
                    :readonly="isReadonly"
                    @input="(val) => handleProductSelect(val, index)"
                  />
                </b-col>
                <!-- Product -->

                <!-- Product Code -->
                <b-col :md="isUpdateMode ? '3' : '4'">
                  <InputField
                    id="product-code"
                    v-model="item.productCode"
                    :label="$t('Product Code')"
                    :placeholder="$t('Enter product code')"
                    type="text"
                    :error-name="$t('Product Code')"
                    rules="required"
                    for="product-code"
                    readonly
                  />
                </b-col>
                <!-- Product Code -->

                <b-col
                  v-if="isUpdateMode"
                  md="3"
                  class="mt-2"
                >
                  <SwitchButton
                    v-if="item.isActive ? getUserPermission('DEACTIVATE_SAVINGS_POCKET_SETUP') : getUserPermission('ACTIVATE_SAVINGS_POCKET_SETUP')"
                    v-model="item.isActive"
                    :label="item.isActive ? $t('Deactivate Savings Type') : $t('Activate Savings Type')"
                    name="enable-savings-pocket-setup"
                    class="ml-6"
                    :readonly="isReadonly"
                    @change.native="value => handleToggleStatus(item, value)"
                  />
                </b-col>

                <!-- Frequent Savings Configuration Fields -->
                <template v-if="isFrequentSavings(item.savingsType)">
                  <b-col :md="isUpdateMode ? '3' : '4'">
                    <InputField
                      :id="`tenure-${index}`"
                      v-model="item.config[0].tenure"
                      :label="$t('Tenure')"
                      :placeholder="$t('Enter tenure')"
                      type="number"
                      :readonly="isReadonly"
                    />
                  </b-col>

                  <b-col :md="isUpdateMode ? '3' : '4'">
                    <InputField
                      :id="`interest-rate-${index}`"
                      v-model="item.config[0].applicableInterestRate"
                      :label="$t('Applicable Interest Rate')"
                      :placeholder="$t('Enter applicable interest rate')"
                      type="number"
                      :readonly="isReadonly"
                    />
                  </b-col>

                  <b-col :md="isUpdateMode ? '3' : '4'">
                    <InputField
                      :id="`liquidation-charge-${index}`"
                      v-model="item.config[0].partLiquidationCharge"
                      :label="$t('Part Liquidation Charge')"
                      :placeholder="$t('Enter part liquidation charge')"
                      type="number"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </template>
              </b-row>

              <div class="text-center mt-3">
                <Button
                  :label="buttonLabel"
                  :loading="isLoading"
                  :disabled="isDisabled"
                  size="lg"
                  @click="handleSubmit"
                />
              </div>

            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BForm, VBTooltip,
} from 'bootstrap-vue'
import { required, positive } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import Button from '../../../components/button/Button.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import { eChannelsPermission } from '../../../../helpers/permissions'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BForm,
    InputField,
    SelectField,
    SwitchButton,
    ValidationObserver,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isBusy: true,
      showSpinner: false,
      isLoading: false,
      isDisabled: false,
      required,
      positive,
      isUpdateMode: false,

      isReadonly: false,
      isEditable: false,
      buttonLabel: this.$t('Save'),
      savingsPocketFormData: [
        {
          id: '',
          savingsType: 'FrequentSavings',
          productCode: null,
          isActive: false,
          config: [
            {
              tenure: 0,
              applicableInterestRate: 0,
              partLiquidationCharge: 0,
            },
          ],
        },
        {
          id: '',
          savingsType: 'FlexSavings',
          productCode: null,
          isActive: false,
        },
        {
          id: '',
          savingsType: 'SmartSavings',
          productCode: null,
          isActive: false,
        },
      ],
      savingsTypeOptions: [
        {
          value: 'FrequentSavings',
          label: this.$t('Frequent Savings'),
        },
        {
          value: 'FlexSavings',
          label: this.$t('Flex Savings'),
        },
        {
          value: 'SmartSavings',
          label: this.$t('Auto Savings'),
        },
      ],
      casaProductOptions: [],
      fdProductOptions: [],

    }
  },
  computed: {
  // Dynamically set readonly state based on button label
    isFormReadonly() {
      return this.buttonLabel !== this.$t('Save') && this.buttonLabel !== this.$t('Save Changes')
    },
  },
  mounted() {
    this.fetchCASAProducts()
    this.fetchFDProducts()
    this.fetchSavingsPocketSetup()
    this.setInitialState()
  },
  methods: {
    ...mapActions({
      getSavingsPocketSetup: 'eChannels/getSavingsPocket',
      createSavingsPocketSetup: 'eChannels/createSavingsPocketSetup',
      updateSavingsPocketSetup: 'eChannels/updateSavingsPocketSetup',
      updateSavingsPocketSetupStatus: 'eChannels/updateSavingsPocketSetupStatus',
      getProducts: 'account/getProducts',
    }),
    // Check if savings type is Frequent Savings
    isFrequentSavings(savingsType) {
      return savingsType === 'FrequentSavings'
    },

    // Get product options based on savings type
    getProductOptions(savingsType) {
      return this.isFrequentSavings(savingsType)
        ? this.fdProductOptions
        : this.casaProductOptions
    },

    // Handle product selection
    handleProductSelect(productCode, index) {
      if (productCode) {
        this.savingsPocketFormData[index].productCode = productCode
      }
    },
    setSavingsType() {
      this.savingsPocketFormData.productCode = null
      return true
    },
    setProductCode(code) {
      this.casaProfileFormData.productCode = code
      return true
    },
    async fetchSavingsPocketSetup() {
      try {
        this.showSpinner = true
        const response = await this.getSavingsPocketSetup({ permission: eChannelsPermission.VIEW_SAVINGS_POCKET_SETUP })
        const { status, data } = response
        if (status === 200 && data.data && data.data.length > 0) {
          this.savingsPocketFormData = data.data.map(item => ({
            id: item.id,
            savingsType: item.savingsType,
            productCode: item.productCode,
            // productName: item.productName,
            isActive: item.isActive,
            dateCreated: item.dateCreated,
            ...(item.savingsType === 'FrequentSavings' && item.config && item.config.length > 0 && {
              config: item.config.map(cfg => ({
                tenure: cfg.tenure || 0,
                applicableInterestRate: cfg.applicableInterestRate || 0,
                partLiquidationCharge: cfg.partLiquidationCharge || 0,
              })),
            }),
          }))
          this.isUpdateMode = true
          this.buttonLabel = this.$t('Edit')
          this.isReadonly = true
          this.isEditable = false
        } else {
          this.isUpdateMode = false
          this.buttonLabel = this.$t('Save')
          this.isReadonly = false
          this.isEditable = true
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getProducts({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: eChannelsPermission.VIEW_SAVINGS_POCKET_SETUP,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.casaProductOptions = data.data.filter(itemValue => itemValue.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchFDProducts() {
      try {
        const response = await this.getProducts({
          search: { type: 'FixedDeposit' },
          page: 0,
          limit: 0,
          permission: eChannelsPermission.VIEW_SAVINGS_POCKET_SETUP,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.fdProductOptions = data.data.filter(itemValue => itemValue.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async handleSubmit() {
      const isSaveMode = this.buttonLabel === this.$t('Save')
      const isSaveChangesMode = this.buttonLabel === this.$t('Save Changes')
      const isEditMode = this.buttonLabel === this.$t('Edit')

      if (isSaveMode || isSaveChangesMode) {
      // Validate and save/update
        const isValid = await this.$refs.savingsPocketRule.validate()
        if (!isValid) return

        const confirmText = isSaveMode
          ? this.$t('Are you sure you want to Save this Savings Pocket Setup?')
          : this.$t('Are you sure you want to Update this Savings Pocket Setup?')

        this.confirm(this.performSavingsPocket, { text: confirmText })
      } else if (isEditMode) {
      // Enable edit mode (Edit → Save Changes)
        this.isReadonly = false
        this.isEditable = true
        this.buttonLabel = this.$t('Save Changes')
      }
    },

    // Set initial readonly state based on button label
    setInitialState() {
      this.isReadonly = this.buttonLabel !== this.$t('Save')
      this.isEditable = this.buttonLabel === this.$t('Save')
    },

    async performSavingsPocket() {
      try {
        this.isLoading = true
        this.showSpinner = true
        this.isDisabled = true
        const payload = {
          configs: this.savingsPocketFormData.map(item => ({
            savingsType: item.savingsType,
            productCode: item.productCode,
            ...(this.isUpdateMode && item.id && {
              id: item.id,
            }),
            ...(this.isFrequentSavings(item.savingsType) && {
              config: [{
                tenure: Number(item.config[0].tenure),
                applicableInterestRate: Number(item.config[0].applicableInterestRate),
                partLiquidationCharge: Number(item.config[0].partLiquidationCharge),
              }],
            }),
          })),
        }
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createSavingsPocketSetup({ payload, permission: eChannelsPermission.CREATE_SAVINGS_POCKET_SETUP })
          : await this.updateSavingsPocketSetup({ payload, permission: eChannelsPermission.MODIFY_SAVINGS_POCKET_SETUP })

        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: this.$t('Your Savings Pocket Setup was updated successfully') })
          this.showSpinner = false
          this.isReadonly = true
          this.isUpdateMode = true
          this.buttonLabel = this.$t('Edit')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.buttonLabel = this.$t('Edit')
        this.isLoading = false
        this.isDisabled = false
        this.showSpinner = false
      }
    },
    async handleToggleStatus(items) {
      const { id, savingsType, isActive } = items
      const actionType = !!items.isActive
      const action = items.isActive ? 'Activate' : 'Deactivate'
      const permission = items.isActive ? eChannelsPermission.DEACTIVATE_SAVINGS_POCKET_SETUP : eChannelsPermission.ACTIVATE_SAVINGS_POCKET_SETUP

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            savingsType,
            isActive,
          }
          const response = await this.updateSavingsPocketSetupStatus({
            statusValues, actionType, routeName: 'SavingsConfig', action, permission,
          })
          if (response.status === 200) {
            this.fetchSavingsPocketSetup()
            const requestText = this.$t('Your request to')
            const successText = this.$t('Savings Pocket Setup was successful')
            this.alertSuccess({ text: `${requestText} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('Savings Pocket Setup')
      this.confirm(updateStatus, { title: '', text: `${text1} ${action} ${text2}?` })
    },
  },
}
</script>

<style scoped>
.message-counter {
  margin-left: 15px;
  margin-top: -10px;
  text-align: right;
}
.tooltip::v-deep .tooltip-inner {
  max-width: 22em;
}
</style>
